import { useEffect, useState, type FormEvent } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { StorefrontBackdrop } from '../components/storefront/chrome'
import { Alert } from '../components/atoms/Alert'
import { Button } from '../components/atoms/Button'
import { Input } from '../components/atoms/Input'
import { Spinner } from '../components/atoms/Spinner'
import { ChangePasswordModal } from '../components/profile/ChangePasswordModal'
import { useAuth } from '../../context/AuthContext'
import { useProfile } from '../../viewmodels/account/useProfile'
import { isValidEmail, isValidMobile, isValidPin } from '../../constants/validators.constants'

/**
 * Profile overlay — the logged-in user's own details as a right-anchored
 * slide-OVER sheet (same UX as the brand StoryOverlay). Edit name / mobile
 * / address and Save; change the email inline (reusing the existing
 * verify-request → verify-confirm flow, then applying the change); change
 * the password via a small popup. Click the dim / × / Esc to close.
 */
type EmailStage = 'idle' | 'enter' | 'code'

export function ProfileOverlay({
  onClose,
  onSaved
}: {
  readonly onClose: () => void
  /** Fired once after a successful saveProfile call. ProfileProvider
   *  uses this to bounce the user back to the cart when the overlay
   *  was opened with `{ returnToCart: true }`. */
  readonly onSaved?: () => void
}): JSX.Element {
  const reduce = useReducedMotion() === true
  const { user, setUserLocal, requestEmailVerification, confirmEmailVerification } = useAuth()
  const { profile, isLoading, error, saveProfile, changePassword, applyEmailChange } = useProfile()

  // ---------- editable form state (hydrated from the loaded profile) ----------
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [line1, setLine1] = useState('')
  const [line2, setLine2] = useState('')
  const [city, setCity] = useState('')
  const [stateName, setStateName] = useState('')
  const [pin, setPin] = useState('')
  const [country, setCountry] = useState('India')

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveOk, setSaveOk] = useState(false)

  const [pwOpen, setPwOpen] = useState(false)

  // ---------- email-change sub-flow ----------
  const [emailStage, setEmailStage] = useState<EmailStage>('idle')
  const [newEmail, setNewEmail] = useState('')
  const [code, setCode] = useState('')
  const [emailBusy, setEmailBusy] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [emailOk, setEmailOk] = useState(false)
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [secondsLeft, setSecondsLeft] = useState(0)

  // Hydrate the form once the profile arrives.
  useEffect(() => {
    if (profile === null) return
    setFirstName(profile.firstName ?? '')
    setLastName(profile.lastName ?? '')
    setPhone(profile.phone ?? '')
    setLine1(profile.address?.line1 ?? '')
    setLine2(profile.address?.line2 ?? '')
    setCity(profile.address?.city ?? '')
    setStateName(profile.address?.state ?? '')
    setPin(profile.address?.pin ?? '')
    setCountry(profile.address?.country ?? 'India')
  }, [profile])

  // Esc closes; lock page scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !pwOpen) onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose, pwOpen])

  // Email-code countdown.
  useEffect(() => {
    if (expiresAt === null) return
    const tick = (): void => {
      setSecondsLeft(Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 1000)))
    }
    tick()
    const h = window.setInterval(tick, 1000)
    return () => window.clearInterval(h)
  }, [expiresAt])

  // ---------- gating ----------
  const nameOk = firstName.trim().length > 0 && lastName.trim().length > 0
  const phoneOk = phone.trim() === '' || isValidMobile(phone)
  const addressTouched =
    line1.trim() !== '' || line2.trim() !== '' || city.trim() !== '' || stateName.trim() !== '' || pin.trim() !== ''
  const addressOk =
    !addressTouched ||
    (line1.trim() !== '' && city.trim() !== '' && stateName.trim() !== '' && isValidPin(pin))
  const canSave = nameOk && phoneOk && addressOk && !saving

  async function handleSave(e: FormEvent): Promise<void> {
    e.preventDefault()
    setSaveError(null)
    setSaveOk(false)
    setSaving(true)
    try {
      const updated = await saveProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        address: {
          line1: line1.trim(),
          line2: line2.trim(),
          city: city.trim(),
          state: stateName.trim(),
          pin: pin.trim(),
          country: country.trim()
        }
      })
      // Keep the app-wide identity (nav greeting etc.) in sync.
      if (user !== null) {
        setUserLocal({ ...user, firstName: updated.firstName, lastName: updated.lastName, phone: updated.phone })
      }
      setSaveOk(true)
      // Notify the provider AFTER state is consistent so it can bounce
      // back to the cart (or whatever opened us).
      if (onSaved !== undefined) onSaved()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save your profile')
    } finally {
      setSaving(false)
    }
  }

  function resetEmailFlow(): void {
    setEmailStage('idle')
    setNewEmail('')
    setCode('')
    setEmailError(null)
    setExpiresAt(null)
  }

  async function sendEmailCode(): Promise<void> {
    setEmailError(null)
    setEmailBusy(true)
    try {
      const { expiresAt: exp } = await requestEmailVerification(newEmail.trim())
      setExpiresAt(exp)
      setCode('')
      setEmailStage('code')
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Could not send the code')
    } finally {
      setEmailBusy(false)
    }
  }

  async function confirmAndApplyEmail(): Promise<void> {
    setEmailError(null)
    setEmailBusy(true)
    try {
      await confirmEmailVerification(newEmail.trim(), code.trim())
      const applied = await applyEmailChange(newEmail.trim())
      if (user !== null) setUserLocal({ ...user, email: applied })
      setEmailOk(true)
      resetEmailFlow()
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Could not verify the code')
    } finally {
      setEmailBusy(false)
    }
  }

  const codeExpired = emailStage === 'code' && secondsLeft === 0 && expiresAt !== null
  const fieldLabel = 'font-body text-body-sm text-text-secondary block mb-1'

  return (
    <motion.div
      className="fixed inset-0 z-50 flex justify-end bg-charcoal/40"
      role="dialog"
      aria-modal="true"
      aria-label="My profile"
      onClick={onClose}
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
      transition={{ duration: 0.4 }}
    >
      <motion.div
        className="relative h-full w-full max-w-2xl overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        variants={{ hidden: { x: reduce ? 0 : '100%' }, visible: { x: 0 } }}
        transition={{ duration: reduce ? 0.2 : 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <StorefrontBackdrop />

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-bg-primary/70 text-charcoal backdrop-blur-md transition hover:text-forest sm:right-6 sm:top-6"
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
            <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <div className="relative z-[1] min-h-full px-5 py-16 sm:px-10">
          <div className="mx-auto max-w-xl rounded-[2rem] border border-border/40 bg-bg-primary/80 px-6 py-9 font-body text-charcoal shadow-lg backdrop-blur-md sm:px-10">
            <h2 className="font-heading text-h2 text-charcoal mb-1">My profile</h2>
            <p className="text-body-sm text-text-secondary mb-6">
              Update your details, email, and password.
            </p>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size="lg" />
              </div>
            ) : error !== null ? (
              <Alert tone="error">{error}</Alert>
            ) : (
              <>
                {/* ── Name + mobile + address (one Save) ── */}
                <form onSubmit={(e) => void handleSave(e)} className="space-y-4" noValidate>
                  {saveError !== null ? <Alert tone="error">{saveError}</Alert> : null}
                  {saveOk ? <Alert tone="success">Profile saved.</Alert> : null}

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <label className="block">
                      <span className={fieldLabel}>First name</span>
                      <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={64} disabled={saving} required />
                    </label>
                    <label className="block">
                      <span className={fieldLabel}>Last name</span>
                      <Input value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={64} disabled={saving} required />
                    </label>
                  </div>

                  <label className="block">
                    <span className={fieldLabel}>Mobile number</span>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      value={phone}
                      hasError={phone.trim() !== '' && !isValidMobile(phone)}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="10-digit mobile"
                      disabled={saving}
                    />
                  </label>

                  <fieldset className="space-y-4 rounded-xl border border-border/50 p-4">
                    <legend className="px-1 font-body text-body-sm text-text-secondary">Address</legend>
                    <label className="block">
                      <span className={fieldLabel}>Address line 1</span>
                      <Input value={line1} onChange={(e) => setLine1(e.target.value)} disabled={saving} />
                    </label>
                    <label className="block">
                      <span className={fieldLabel}>Address line 2 (optional)</span>
                      <Input value={line2} onChange={(e) => setLine2(e.target.value)} disabled={saving} />
                    </label>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <label className="block">
                        <span className={fieldLabel}>City</span>
                        <Input value={city} onChange={(e) => setCity(e.target.value)} disabled={saving} />
                      </label>
                      <label className="block">
                        <span className={fieldLabel}>State</span>
                        <Input value={stateName} onChange={(e) => setStateName(e.target.value)} disabled={saving} />
                      </label>
                      <label className="block">
                        <span className={fieldLabel}>PIN code</span>
                        <Input
                          inputMode="numeric"
                          value={pin}
                          hasError={pin.trim() !== '' && !isValidPin(pin)}
                          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          disabled={saving}
                        />
                      </label>
                      <label className="block">
                        <span className={fieldLabel}>Country</span>
                        <Input value={country} onChange={(e) => setCountry(e.target.value)} disabled={saving} />
                      </label>
                    </div>
                  </fieldset>

                  <Button type="submit" variant="primary" size="lg" disabled={!canSave} className="w-full">
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                </form>

                {/* ── Email ── */}
                <div className="mt-8 border-t border-border/50 pt-6">
                  <h3 className="font-heading text-h4 text-charcoal mb-2">Email</h3>
                  {emailOk ? <div className="mb-3"><Alert tone="success">Email updated.</Alert></div> : null}
                  <p className="text-body-sm text-text-secondary mb-3">
                    Current: <span className="text-charcoal">{profile?.email}</span>
                  </p>

                  {emailStage === 'idle' ? (
                    <Button type="button" variant="secondary" size="md" onClick={() => { setEmailOk(false); setEmailStage('enter') }}>
                      Change email
                    </Button>
                  ) : (
                    <div className="space-y-3 rounded-xl border border-border/50 p-4">
                      {emailError !== null ? <Alert tone="error">{emailError}</Alert> : null}
                      <label className="block">
                        <span className={fieldLabel}>New email</span>
                        <Input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          disabled={emailStage === 'code' || emailBusy}
                          required
                        />
                      </label>

                      {emailStage === 'enter' ? (
                        <div className="flex gap-2">
                          <Button type="button" variant="primary" size="md" disabled={!isValidEmail(newEmail) || emailBusy} onClick={() => void sendEmailCode()} className="flex-1">
                            {emailBusy ? 'Sending…' : 'Send code'}
                          </Button>
                          <Button type="button" variant="secondary" size="md" onClick={resetEmailFlow} disabled={emailBusy} className="flex-1">
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <label className="block">
                            <span className={fieldLabel}>6-digit code</span>
                            <Input
                              inputMode="numeric"
                              value={code}
                              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              placeholder="••••••"
                              disabled={emailBusy}
                            />
                            <span className="mt-1 block text-body-xs text-text-secondary">
                              {codeExpired ? 'Code expired.' : `Sent to ${newEmail.trim()} · expires in ${formatMmSs(secondsLeft)}`}
                            </span>
                          </label>
                          <div className="flex gap-2">
                            {codeExpired ? (
                              <Button type="button" variant="secondary" size="md" onClick={() => void sendEmailCode()} disabled={emailBusy} className="flex-1">
                                Resend code
                              </Button>
                            ) : (
                              <Button type="button" variant="primary" size="md" disabled={code.length !== 6 || emailBusy} onClick={() => void confirmAndApplyEmail()} className="flex-1">
                                {emailBusy ? 'Verifying…' : 'Verify & update'}
                              </Button>
                            )}
                            <Button type="button" variant="secondary" size="md" onClick={resetEmailFlow} disabled={emailBusy} className="flex-1">
                              Cancel
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Password ── */}
                <div className="mt-8 border-t border-border/50 pt-6">
                  <h3 className="font-heading text-h4 text-charcoal mb-2">Password</h3>
                  <Button type="button" variant="secondary" size="md" onClick={() => setPwOpen(true)}>
                    Change password
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {pwOpen ? (
        <ChangePasswordModal onClose={() => setPwOpen(false)} onSubmit={changePassword} />
      ) : null}
    </motion.div>
  )
}

function formatMmSs(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
